import { useEffect, useState } from 'react';
import {
  Button, Card, Form, Input, Select, Layout, Typography, Divider,
  Row, Col, Switch, message, Modal, Spin, Steps, Space, Alert, ConfigProvider
} from 'antd';
import {
  UserOutlined, PhoneOutlined, MailOutlined, ShopOutlined,
  EnvironmentOutlined, DeleteOutlined, LockOutlined, SafetyOutlined,
  CheckCircleOutlined, ArrowRightOutlined, PlusOutlined
} from '@ant-design/icons';
import { BACKENDURL } from '../../components/constants';
import axiosInstance from '../../axiosInstance';
import { useLocation } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { Content } = Layout;
const { Step } = Steps;

// Custom theme configuration
const theme = {
  token: {
    colorPrimary: '#ff4f2e',
    colorInfo: '#0d306d',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorTextBase: '#0d306d',
    colorBgBase: '#ffffff',
    borderRadius: 8,
  },
  components: {
    Button: {
      colorPrimary: '#ff4f2e',
      colorPrimaryHover: '#ff6e52',
      colorPrimaryActive: '#e5462a',
    },
    Steps: {
      colorPrimary: '#ff4f2e',
    },
    Switch: {
      colorPrimary: '#ff4f2e',
    },
  },
};

const RegisterPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isTrial = queryParams.get('isTrial');
  const [form] = Form.useForm();
  const [shopCount, setShopCount] = useState(1);
  const [warehouseCount, setWarehouseCount] = useState(0);
  const [hasWarehouse, setHasWarehouse] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trialPackageId, setTrialPackageId] = useState(null);
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [emailToVerify, setEmailToVerify] = useState('');

  const sendOtp = async (email) => {
    try {
      setOtpLoading(true);
      const response = await axiosInstance.post(`${BACKENDURL}/send-otp`, { email });
      message.success(response.data.message || 'OTP sent to your email!');
      setEmailToVerify(email);
      setOtpModalVisible(true);
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (otp) => {
    try {
      setOtpLoading(true);
      const response = await axiosInstance.post(`${BACKENDURL}/verify-otp`, {
        email: emailToVerify,
        otp
      });
      message.success(response.data.message || 'Email verified successfully!');
      setOtpVerified(true);
      setOtpModalVisible(false);
      setCurrentStep(1);
    } catch (error) {
      message.error(error.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (loading || !otpVerified) return;
    setLoading(true);

    const fullName = `${values.firstName} ${values.lastName}`;

    const finalData = {
      ...values,
      fullName,
      user_name: fullName,
      user_email: values.email,
      user_password: values.password,
      user_phone: values.phone,
      haveWarehouse: hasWarehouse,
      is_trial: isTrial ? 1 : 0,
      otp: "verified"
    };

    delete finalData.firstName;
    delete finalData.lastName;
    delete finalData.confirmPassword;
    delete finalData.password;

    try {
      const res = await axiosInstance.post(`${BACKENDURL}/register-retailer`, finalData);
      message.destroy();

      if (res.status === 201) {
        message.success(res.data.message || 'Registration successful!');
        form.resetFields();
        setShopCount(1);
        setWarehouseCount(0);
        setHasWarehouse(false);
        setSelectedPackage(null);
        setCurrentStep(2);
      } else {
        message.error(res.data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      message.destroy();
      const response = error.response;
      if (response?.status === 422 && response.data?.errors) {
        const allErrors = Object.values(response.data.errors).flat();
        allErrors.forEach(err => message.error(err));
      } else {
        message.error(response?.data?.message || 'An error occurred during registration.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCheck = async () => {
    const email = form.getFieldValue('email');
    if (!email) {
      message.error('Please enter your email first');
      return;
    }

    try {
      setOtpLoading(true);
      await sendOtp(email);
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    axiosInstance.get(`${BACKENDURL}/business-types`)
      .then(response => setBusinessTypes(response.data.business_types))
      .catch(error => console.error('Error fetching business types:', error));

    axiosInstance.get(`${BACKENDURL}/packages`)
      .then(response => {
        const fetchedPackages = response.data.packages;
        setPackages(fetchedPackages);

        const trial = fetchedPackages.find(p => p.name.toLowerCase() === 'trial');
        if (trial) {
          setTrialPackageId(trial.id);
          if (isTrial === '1') {
            form.setFieldsValue({ packageId: trial.id });
          }
        }
      })
      .catch(error => console.error('Error fetching packages:', error));
  }, []);

  const steps = [
    {
      title: 'Email Verification',
      content: (
        <Card 
          style={{ 
            marginTop: 16,
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: 32 }}
        >
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={3} style={{ color: '#0d306d' }}>
              Verify Your Email
            </Title>
            <Text type="secondary">
              We'll send a verification code to your email to secure your account
            </Text>
          </div>

          <Form form={form} layout="vertical" size="large">
            <Form.Item
              name="email"
              label={<Text strong>Email Address</Text>}
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                placeholder="your@email.com"
                disabled={otpVerified}
              />
            </Form.Item>
            
            <Form.Item>
              <Button
                type="primary"
                onClick={handleEmailCheck}
                loading={otpLoading}
                disabled={otpVerified}
                icon={otpVerified ? <CheckCircleOutlined /> : <SafetyOutlined />}
                size="large"
                block
                style={{
                  background: '#ff4f2e',
                  borderColor: '#ff4f2e',
                  fontWeight: 500
                }}
              >
                {otpVerified ? 'Email Verified' : 'Send Verification Code'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      title: 'Registration Form',
      content: (
        <Card 
          style={{ 
            width: '100%', 
            maxWidth: 800, 
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: 32 }}
        >
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={3} style={{ color: '#0d306d' }}>
              Create Your Account
            </Title>
            <Text type="secondary">
              Join thousands of businesses using SellSmart to grow their operations
            </Text>
          </div>

          <Form form={form} layout="vertical" onFinish={onFinish} size="large">
            {/* Contact Info */}
            <Card 
              title={<Text strong style={{ fontSize: 16, color: '#0d306d' }}>Contact Information</Text>}
              bordered={false}
              style={{ 
                marginBottom: 24, 
                background: '#f9fafc',
                borderRadius: 8
              }}
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    name="firstName" 
                    label={<Text strong>First Name</Text>} 
                    rules={[{ required: true }]}
                  >
                    <Input 
                      prefix={<UserOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                      placeholder="John" 
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    name="lastName" 
                    label={<Text strong>Last Name</Text>} 
                    rules={[{ required: true }]}
                  >
                    <Input 
                      prefix={<UserOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                      placeholder="Doe" 
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    name="phone" 
                    label={<Text strong>Phone Number</Text>} 
                    rules={[{ required: true }]}
                  >
                    <Input 
                      prefix={<PhoneOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                      placeholder="255 234 567 890" 
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    name="email" 
                    label={<Text strong>Email</Text>} 
                    rules={[{ required: true, type: 'email' }]}
                  >
                    <Input 
                      prefix={<MailOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                      placeholder="your@email.com" 
                      disabled 
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    name="password" 
                    label={<Text strong>Password</Text>} 
                    rules={[{ required: true, min: 6 }]} 
                    hasFeedback
                  >
                    <Input.Password 
                      prefix={<LockOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                      placeholder="Password" 
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="confirmPassword"
                    label={<Text strong>Confirm Password</Text>}
                    dependencies={['password']}
                    hasFeedback
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Passwords do not match!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password 
                      prefix={<LockOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                      placeholder="Confirm Password" 
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Business Details */}
            <Card 
              title={<Text strong style={{ fontSize: 16, color: '#0d306d' }}>Business Details</Text>}
              bordered={false}
              style={{ 
                marginBottom: 24, 
                background: '#f9fafc',
                borderRadius: 8
              }}
              bodyStyle={{ padding: 16 }}
            >
              <Form.Item 
                name="businessName" 
                label={<Text strong>Business Name</Text>} 
                rules={[{ required: true }]}
              >
                <Input 
                  prefix={<ShopOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                  placeholder="My Retail Store" 
                />
              </Form.Item>

              <Form.Item 
                name="businessType" 
                label={<Text strong>Business Type</Text>} 
                rules={[{ required: true }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select business types"
                  loading={businessTypes.length === 0}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {businessTypes.map(type => (
                    <Option key={type.id} value={type.id}>{type.name}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="packageId"
                label={<Text strong>Select Package</Text>}
                rules={[{ required: true }]}
                initialValue={isTrial === 1 ? trialPackageId : undefined}
              >
                <Select
                  placeholder="Choose a package"
                  loading={packages.length === 0}
                  disabled={isTrial === '1'}
                  onChange={(value) => {
                    const pkg = packages.find(p => p.id === value);
                    setSelectedPackage(pkg);
                    setShopCount(1);
                    setWarehouseCount(0);
                  }}
                >
                  {packages.map(pkg => (
                    <Option key={pkg.id} value={pkg.id}>
                      {pkg.name} - {pkg.price} TZS ({pkg.shops_count} shop(s))
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Card>

            {/* Shops */}
            <Card 
              title={
                <Space>
                  <Text strong style={{ fontSize: 16, color: '#0d306d' }}>Shop Locations</Text>
                  <Text type="secondary">({shopCount} added)</Text>
                </Space>
              }
              bordered={false}
              style={{ 
                marginBottom: 24, 
                background: '#f9fafc',
                borderRadius: 8
              }}
              bodyStyle={{ padding: 16 }}
            >
              {[...Array(shopCount)].map((_, i) => (
                <Card 
                  key={i} 
                  style={{ 
                    marginBottom: 16, 
                    borderRadius: 8,
                    borderLeft: '4px solid #ff4f2e'
                  }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Title level={5} style={{ marginBottom: 16, color: '#0d306d' }}>Shop {i + 1}</Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item 
                        name={['shops', i, 'name']} 
                        label={<Text strong>Name</Text>} 
                        rules={[{ required: true }]}
                      >
                        <Input 
                          prefix={<EnvironmentOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                          placeholder="Shop name" 
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name={['shops', i, 'city']} 
                        label={<Text strong>City</Text>} 
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="City" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}

              <Button
                type="dashed"
                onClick={() => {
                  if (!selectedPackage || shopCount < selectedPackage.shops_count) {
                    setShopCount(prev => prev + 1);
                  }
                }}
                disabled={!selectedPackage || shopCount >= selectedPackage.shops_count}
                block
                icon={<PlusOutlined />}
                style={{ color: '#0d306d' }}
              >
                Add Another Shop
              </Button>
              {selectedPackage && shopCount >= selectedPackage.shops_count && (
                <Alert
                  message="You have reached the shop limit for the selected package."
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </Card>

            {/* Warehouses */}
            <Card 
              title={<Text strong style={{ fontSize: 16, color: '#0d306d' }}>Warehouse Information</Text>}
              bordered={false}
              style={{ 
                marginBottom: 24, 
                background: '#f9fafc',
                borderRadius: 8
              }}
              bodyStyle={{ padding: 16 }}
            >
              <Form.Item label={<Text strong>Do you have a warehouse?</Text>}>
                <Switch 
                  checked={hasWarehouse} 
                  onChange={setHasWarehouse} 
                  checkedChildren="Yes" 
                  unCheckedChildren="No" 
                />
              </Form.Item>

              {hasWarehouse && (
                <>
                  <Form.List name="warehouses">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Card 
                            key={key} 
                            style={{ 
                              marginBottom: 16,
                              borderRadius: 8,
                              borderLeft: '4px solid #ff4f2e'
                            }}
                            bodyStyle={{ padding: 16 }}
                          >
                            <Row gutter={16} align="middle">
                              <Col span={10}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'location']}
                                  label={<Text strong>Location</Text>}
                                  rules={[{ required: true }]}
                                >
                                  <Input 
                                    prefix={<EnvironmentOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />} 
                                    placeholder="e.g. Industrial Area" 
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={10}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'size']}
                                  label={<Text strong>Size (sq ft)</Text>}
                                  rules={[{ required: true }]}
                                >
                                  <Input type="number" placeholder="e.g. 5000" />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    remove(name);
                                    setWarehouseCount(prev => prev - 1);
                                  }}
                                  style={{ float: 'right' }}
                                />
                              </Col>
                            </Row>
                          </Card>
                        ))}
                        <Form.Item>
                          <Button
                            type="dashed"
                            block
                            onClick={() => {
                              if (selectedPackage?.count_warehouse && warehouseCount < selectedPackage.count_warehouse) {
                                add();
                                setWarehouseCount(prev => prev + 1);
                              }
                            }}
                            disabled={!selectedPackage || warehouseCount >= selectedPackage.count_warehouse}
                            icon={<PlusOutlined />}
                            style={{ color: '#0d306d' }}
                          >
                            Add Warehouse
                          </Button>
                          {selectedPackage && warehouseCount >= selectedPackage.count_warehouse && (
                            <Alert
                              message="You have reached the warehouse limit for this package."
                              type="info"
                              showIcon
                              style={{ marginTop: 16 }}
                            />
                          )}
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </>
              )}
            </Card>

            <Form.Item style={{ marginTop: 32 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading} 
                block
                size="large"
                icon={<ArrowRightOutlined />}
                style={{
                  background: '#ff4f2e',
                  borderColor: '#ff4f2e',
                  fontWeight: 500
                }}
              >
                Complete Registration
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      title: 'Registration Complete',
      content: (
        <Card 
          style={{ 
            marginTop: 16, 
            textAlign: 'center',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: 40 }}
        >
          <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
          <Title level={3} style={{ marginBottom: 16, color: '#0d306d' }}>
            Registration Successful!
          </Title>
          <Text style={{ fontSize: 16, display: 'block', marginBottom: 32 }}>
            Thank you for registering with SellSmart. Your account has been created successfully.
          </Text>
          <Button 
            type="primary" 
            href="/login"
            size="large"
            style={{
              background: '#ff4f2e',
              borderColor: '#ff4f2e',
              fontWeight: 500
            }}
          >
            Proceed to Login
          </Button>
        </Card>
      ),
    },
  ];

  return (
    <ConfigProvider theme={theme}>
      <Layout style={{ minHeight: '100vh', background: '#ffffff', marginTop: 60 }}>
        <Content style={{ padding: '24px 0', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 800 }}>
            <div style={{ 
              background: '#fff', 
              padding: '24px', 
              borderRadius: 12, 
              marginBottom: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <Steps 
                current={currentStep} 
                style={{ marginBottom: 0 }}
                items={steps.map((item, index) => ({
                  title: (
                    <span style={{ 
                      fontSize: index === currentStep ? '14px' : '13px',
                      fontWeight: index === currentStep ? 600 : 400,
                      color: index === currentStep ? '#0d306d' : 'rgba(0,0,0,0.45)'
                    }}>
                      {item.title}
                    </span>
                  ),
                }))}
              />
            </div>
            
            <div style={{ padding: '0 24px' }}>
              {steps[currentStep].content}
            </div>

            {/* OTP Verification Modal */}
            <Modal
              title={
                <Space>
                  <SafetyOutlined style={{ color: '#ff4f2e' }} />
                  <Text strong>Email Verification</Text>
                </Space>
              }
              open={otpModalVisible}
              onCancel={() => setOtpModalVisible(false)}
              footer={null}
              destroyOnClose
              centered
            >
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Text style={{ display: 'block', marginBottom: 24 }}>
                  We've sent a 6-digit verification code to <Text strong>{emailToVerify}</Text>
                </Text>
                <Form
                  onFinish={(values) => verifyOtp(values.otp)}
                >
                  <Form.Item
                    name="otp"
                    rules={[
                      { required: true, message: 'Please enter the OTP' },
                      { len: 6, message: 'OTP must be 6 digits' }
                    ]}
                  >
                    <Input
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      prefix={<SafetyOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
                      style={{ 
                        textAlign: 'center', 
                        fontSize: 18,
                        letterSpacing: 8,
                        height: 48
                      }}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={otpLoading}
                      block
                      size="large"
                      style={{
                        background: '#ff4f2e',
                        borderColor: '#ff4f2e',
                        fontWeight: 500
                      }}
                    >
                      Verify OTP
                    </Button>
                  </Form.Item>
                </Form>
                <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                  Didn't receive the code?
                </Text>
                <Button
                  type="link"
                  onClick={() => sendOtp(emailToVerify)}
                  loading={otpLoading}
                  icon={<MailOutlined />}
                  style={{ color: '#0d306d' }}
                >
                  Resend OTP
                </Button>
              </div>
            </Modal>
          </div>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default RegisterPage;